import type { EmailAccountListItem, Tag, WorkspaceTag } from '@/lib/email-accounts';

/** Max tags per account — mirrors the backend cap on `PUT :id/tags`. */
export const MAX_TAGS_PER_ACCOUNT = 25;

/**
 * Reserved sentinel for the filter drawer's "Without Tags" option — rides
 * inside the normal `tags` array/URL param instead of a separate field. Must
 * match the backend copy in apps/api/src/modules/tags/constants/reserved-tag-names.constants.ts.
 */
export const WITHOUT_TAGS_FILTER_VALUE = 'without_tags';

/** Max tag name length — mirrors `Tag.name` (`varchar(50)`) in packages/database. */
export const MAX_TAG_NAME_LENGTH = 50;

/** Trim + collapse whitespace for case-insensitive comparison. */
export function normalizeTagName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

/** Case-insensitive, whitespace-insensitive name equality. */
export function tagNameEquals(a: string, b: string): boolean {
  return normalizeTagName(a).toLowerCase() === normalizeTagName(b).toLowerCase();
}

/** Does `names` already contain `candidate` (case-insensitive)? */
export function hasTagName(names: string[], candidate: string): boolean {
  return names.some((n) => tagNameEquals(n, candidate));
}

/**
 * Filter the workspace catalog by a typed query (case-insensitive substring).
 * An empty query returns the full catalog.
 */
export function filterTagsByQuery(
  tags: WorkspaceTag[],
  query: string,
): WorkspaceTag[] {
  const q = normalizeTagName(query).toLowerCase();
  if (q.length === 0) return tags;
  return tags.filter((t) => t.name.toLowerCase().includes(q));
}

/**
 * Toggle a tag name in/out of a desired-names set (case-insensitive). Returns a
 * new array; preserves the original casing of existing entries.
 */
export function toggleTagName(names: string[], name: string): string[] {
  const normalized = normalizeTagName(name);
  if (hasTagName(names, normalized)) {
    return names.filter((n) => !tagNameEquals(n, normalized));
  }
  return [...names, normalized];
}

/**
 * Intersection of tags across the given accounts — the tags present on *every*
 * account. Used by the bulk modal's Remove section (loaded-row intersection).
 * Dedupes by lowercased name, keeping the first-seen `Tag` for color/label.
 */
export function commonTags(accounts: EmailAccountListItem[]): Tag[] {
  if (accounts.length === 0) return [];
  const [first, ...rest] = accounts;
  const seen = new Map<string, Tag>();
  for (const tag of first.tags ?? []) {
    const key = tag.name.toLowerCase();
    if (!seen.has(key)) seen.set(key, tag);
  }
  for (const account of rest) {
    const accountKeys = new Set((account.tags ?? []).map((t) => t.name.toLowerCase()));
    for (const key of [...seen.keys()]) {
      if (!accountKeys.has(key)) seen.delete(key);
    }
    if (seen.size === 0) break;
  }
  return [...seen.values()];
}
